import type { StaffSummary } from '@school-erp/types';
import { createStaffSchema } from '@school-erp/validation';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  SchoolSelect,
  errorMessage,
  primaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';

export function StaffPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [schoolId, setSchoolId] = useState('');
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const staff = useQuery({
    queryKey: ['staff', { schoolId, page, q }],
    queryFn: ({ signal }) =>
      apiList<StaffSummary>('/staff', {
        query: { schoolId, page, pageSize: 10, q: q.trim() },
        signal,
      }),
    enabled: !!schoolId,
    placeholderData: keepPreviousData,
  });

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{t('staff.title')}</h1>
        {can('staff.create', schoolId) && (
          <button
            type="button"
            aria-expanded={adding}
            className={primaryButton}
            onClick={() => setAdding((o) => !o)}
          >
            {adding ? t('common.cancel') : t('staff.add')}
          </button>
        )}
      </div>
      <SchoolSelect
        value={schoolId}
        onChange={(id) => {
          setSchoolId(id);
          setPage(1);
        }}
      />
      {notice && <Notice kind="ok">{notice}</Notice>}
      {adding && schoolId && (
        <AddStaff
          schoolId={schoolId}
          onCreated={() => {
            setAdding(false);
            setNotice(t('staff.created'));
            void queryClient.invalidateQueries({ queryKey: ['staff'] });
          }}
        />
      )}
      <div>
        <label htmlFor="staff-search" className="block text-sm font-medium">
          {t('users.search')}
        </label>
        <input
          id="staff-search"
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="mt-1 rounded border border-slate-300 bg-white px-3 py-2 text-sm"
        />
      </div>
      {staff.isPending && schoolId && <p role="status">{t('common.loading')}</p>}
      {staff.data && (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th scope="col" className="px-4 py-3">
                    {t('staff.number')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('users.name')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('staff.designation')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('staff.type')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {staff.data.data.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-slate-600">
                      {t('staff.empty')}
                    </td>
                  </tr>
                )}
                {staff.data.data.map((s) => (
                  <tr key={s.id}>
                    <td className="px-4 py-3 font-mono text-xs">{s.employeeNumber}</td>
                    <td className="px-4 py-3 font-medium">
                      {s.firstName} {s.lastName}
                    </td>
                    <td className="px-4 py-3">{s.designation ?? '—'}</td>
                    <td className="px-4 py-3">{t(`staff.${s.staffType}` as MessageKey)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} totalPages={staff.data.meta.totalPages} onPage={setPage} />
        </>
      )}
    </div>
  );
}

function AddStaff({ schoolId, onCreated }: { schoolId: string; onCreated: () => void }) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    designation: '',
    staffType: 'teaching',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: (body: object) => api('/staff', { method: 'POST', body }),
    onSuccess: onCreated,
    onError: (e) => setMessage(errorMessage(e)),
  });
  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const candidate = Object.fromEntries(
          Object.entries({ schoolId, ...form }).filter(([, v]) => v !== ''),
        );
        const parsed = createStaffSchema.safeParse(candidate);
        if (!parsed.success) {
          setErrors(
            Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])),
          );
          return;
        }
        setErrors({});
        create.mutate(parsed.data);
      }}
      className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
    >
      <TextField
        label={t('students.firstName')}
        value={form.firstName}
        error={errors.firstName}
        onChange={(e) => setForm({ ...form, firstName: e.target.value })}
      />
      <TextField
        label={t('students.lastName')}
        value={form.lastName}
        error={errors.lastName}
        onChange={(e) => setForm({ ...form, lastName: e.target.value })}
      />
      <TextField
        label={t('staff.designation')}
        value={form.designation}
        onChange={(e) => setForm({ ...form, designation: e.target.value })}
      />
      <div>
        <label htmlFor="staff-type" className="block text-sm font-medium">
          {t('staff.type')}
        </label>
        <select
          id="staff-type"
          value={form.staffType}
          onChange={(e) => setForm({ ...form, staffType: e.target.value })}
          className={selectClass}
        >
          <option value="teaching">{t('staff.teaching')}</option>
          <option value="non_teaching">{t('staff.non_teaching')}</option>
        </select>
      </div>
      <div className="space-y-2 self-end sm:col-span-2">
        {message && <Notice kind="error">{message}</Notice>}
        <button type="submit" disabled={create.isPending} className={primaryButton}>
          {t('adm.save')}
        </button>
      </div>
    </form>
  );
}
